import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from './Card';
import {
  Filter,
  AlertTriangle,
  Inbox,
  BadgeCheck,
  Timer,
  ArrowRight,
  Clock,
  Hourglass,
  Lightbulb,
  Percent,
  Info,
  ChevronDown,
  Download,
} from 'lucide-react';
import { FUNNEL } from '../data';
import { TONES } from '../theme';
import useCountUp from '../hooks/useCountUp';
import StageQueueDrawer from './StageQueueDrawer';
import DateRangePicker, { DATE_RANGE_PRESETS } from './DateRangePicker';
const BASE_URL = process.env.REACT_APP_BASE_URL;

const FALLBACK_COLORS = ['#CBD5E1', '#E2E8F0'];
const colorsOf = (s) => s.colors || FALLBACK_COLORS;

/* Minimum width (%) drawn for a stage with zero cases, so it stays clickable. */
const MIN_SEG_PCT = 1.2;

const STAGE_TONES = [
  TONES.slate,
  TONES.amber,
  TONES.blue,
  TONES.violet,
  TONES.violet,
  TONES.green,
];

/* FUNNEL is a 30-day snapshot; scale proportionally for other windows. */
const baseDays = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/* ---------- API wiring (GET /api/v1/agency/dashboard/funnel) ---------- */

const FUNNEL_ENDPOINT = '/api/v1/agency/dashboard/funnel';
const STAGE_QUEUE_ENDPOINT = (stageKey) =>
  `${FUNNEL_ENDPOINT}/stages/${stageKey}`;

/* `days` only accepts 7, 30 or 90 — snap any other window to the nearest one
   (the stage-queue endpoint has no start_date/end_date support). */
const API_DAY_OPTIONS = [7, 30, 90];
const closestApiDays = (value) =>
  API_DAY_OPTIONS.reduce(
    (best, d) => (Math.abs(d - value) < Math.abs(best - value) ? d : best),
    API_DAY_OPTIONS[1],
  );

/* Preset ids like "7d" / "30d" / "90d" / "last-90-days" → allowed API value */
const presetDaysFromId = (id) => {
  const match = /(\d+)/.exec(id || '');
  const found = match ? Number(match[1]) : null;
  return found && API_DAY_OPTIONS.includes(found) ? found : null;
};

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/* Gradient pair per stage key returned by the API (same palette the card used before). */
const STAGE_COLORS = {
  onboarding: ['#CBD5E1', '#94A3B8'],
  pending: ['#FDE68A', '#F59E0B'],
  in_review: ['#BFDBFE', '#3B82F6'],
  resubmission: ['#DDD6FE', '#8B5CF6'],
  approved: ['#A7F3D0', '#10B981'],
};
const STAGE_COLOR_SEQUENCE = [
  STAGE_COLORS.onboarding,
  STAGE_COLORS.pending,
  STAGE_COLORS.in_review,
  STAGE_COLORS.resubmission,
  STAGE_COLORS.approved,
];

/* Map the funnel payload onto the exact shape the card already renders. */
const mapFunnelPayload = (payload) => {
  const stages = Array.isArray(payload?.stages) ? payload.stages : [];
  const totals = payload?.totals || {};
  const bottleneckKey = payload?.bottleneck?.key ?? null;
  const mode = payload?.mode || 'cases';
  const totalCustomers =
    num(totals.total_customers) ||
    stages.reduce((sum, s) => sum + num(s.count), 0);

  const mapped = stages.map((st, idx) => {
    const share = num(st.share_percent);
    const rawCount =
      num(st.count) || Math.round((share / 100) * totalCustomers) || 0;

    /* In `mode=share` the backend may return share-weighted values in `count`;
       reconstruct counts from share_percent so the bar geometry stays correct. */
    const count =
      mode === 'share' && totalCustomers
        ? Math.round((share / 100) * totalCustomers)
        : rawCount;

    /* avg / oldest / step_conversion come back as null for empty stages — keep
       them null so the UI shows "—" instead of a misleading 0d / 0%. */
    const hasAvg = st.average_days_in_stage != null;
    const hasOldest = st.oldest_days_in_stage != null;
    const avgDays = hasAvg ? num(st.average_days_in_stage) : 0;
    const oldestDays = hasOldest ? num(st.oldest_days_in_stage) : 0;
    const sharePct =
      share || (totalCustomers ? (count / totalCustomers) * 100 : 0);
    const isBottleneck = bottleneckKey ? st.key === bottleneckKey : false;
    const risk = count > 0 && (isBottleneck || oldestDays >= 30);
    const label = st.label || st.key || `Stage ${idx + 1}`;

    return {
      key: st.key || label,
      label,
      value: count,
      colors:
        STAGE_COLORS[st.key] ||
        STAGE_COLOR_SEQUENCE[idx % STAGE_COLOR_SEQUENCE.length],
      sharePercent: sharePct,
      stepConversion:
        st.step_conversion_percent != null
          ? num(st.step_conversion_percent)
          : null,
      avg: hasAvg ? `${avgDays}d` : '—',
      oldest: hasOldest ? `${oldestDays}d` : '—',
      avgDays,
      oldestDays,
      bottleneck: isBottleneck,
      slaRisk: isBottleneck ? count : undefined,
      sub:
        count === 0
          ? 'no cases'
          : risk
            ? `${oldestDays}d oldest`
            : `${avgDays}d avg`,
      subTone: risk ? 'risk' : undefined,
      desc:
        count === 0
          ? `${label}: no cases in this window.`
          : isBottleneck && payload?.insight?.message
            ? payload.insight.message
            : `${label}: ${count.toLocaleString('en-AU')} case(s), averaging ${avgDays}d in stage (oldest ${oldestDays}d), ${sharePct.toFixed(1)}% of all customers.`,
    };
  });

  return {
    days: num(payload?.days, baseDays),
    mode,
    stages: mapped,
    insight: payload?.insight || null,
    totals: {
      totalCustomers,
      inPipeline: totals.in_pipeline != null ? num(totals.in_pipeline) : null,
      approved: totals.approved != null ? num(totals.approved) : null,
      approvedInWindow:
        totals.approved_in_window != null
          ? num(totals.approved_in_window)
          : null,
      conversionPercent:
        totals.conversion_percent != null
          ? num(totals.conversion_percent)
          : null,
      medianCycleDays:
        totals.median_cycle_days != null ? num(totals.median_cycle_days) : null,
      medianCycleBasis: totals.median_cycle_basis || null,
    },
  };
};

const toInputDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const relativeInputDate = (offsetDays) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return toInputDate(date);
};

const formatDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getDateRangeDays = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const days = Math.round((endDate - startDate) / DAY_MS) + 1;
  return days > 0 ? days : null;
};

const fmt = (n) => n.toLocaleString('en-AU');

/* Animated mini stat used in the summary strip */
function MiniStat({
  icon: Icon,
  tone,
  label,
  value,
  suffix,
  delay,
  sub,
  display,
}) {
  const n = useCountUp(value, 900 + delay);
  return (
    <div
      className="animate-fade-up group rounded-xl border border-slate-200/80 bg-white px-3.5 py-3 hover:border-slate-300 hover:shadow-card transition-all duration-200"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-7 h-7 rounded-lg ${tone.tile} flex items-center justify-center transition-transform duration-200 group-hover:scale-110`}
        >
          <Icon size={13} strokeWidth={1.9} />
        </span>
        <p className="text-[11px] font-medium text-slate-500">{label}</p>
      </div>
      <p className="mt-2 text-xl font-bold text-slate-900 tabular-nums leading-6">
        {display != null ? display : n}
        {suffix && (
          <span className="ml-1 text-xs font-semibold text-slate-400">
            {suffix}
          </span>
        )}
      </p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

export default function FunnelCard() {
  const navigate = useNavigate();
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [queueStage, setQueueStage] = useState(null);
  const [range, setRange] = useState('30d'); // 7d | 30d | 90d | custom
  const [dateRange, setDateRange] = useState(() => ({
    start: relativeInputDate(-(baseDays - 1)),
    end: relativeInputDate(0),
  }));
  const [metric, setMetric] = useState('cases'); // cases | share
  const [fold, setFold] = useState(false); // how-to-read
  const [downloaded, setDownloaded] = useState(false);

  /* ---------- API state ---------- */
  const [apiFunnel, setApiFunnel] = useState(null); // GET /dashboard/funnel payload
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const customDays = useMemo(
    () => getDateRangeDays(dateRange.start, dateRange.end),
    [dateRange],
  );
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const dateError =
    !dateRange.start || !dateRange.end
      ? 'Select both a start and end date.'
      : !customDays
        ? 'End date must be on or after the start date.'
        : '';

  const days = customDays ?? baseDays;
  /* picker label first (it always matches what the user just chose), then the
     payload's own range.label as a fallback */
  const rangeLabel =
    (range === 'custom'
      ? customDays
        ? `${formatDateLabel(dateRange.start)} – ${formatDateLabel(
            dateRange.end,
          )}`
        : 'Custom range'
      : DATE_RANGE_PRESETS.find((r) => r.id === range)?.label) ||
    apiFunnel?.range?.label ||
    'Last 30 days';

  /* ---------- Request URL: days= for presets, start_date/end_date for custom ---------- */
  const requestUrl = useMemo(() => {
    const mode = metric === 'share' ? 'share' : 'cases';
    if (range === 'custom') {
      if (!customDays) return null; // invalid range → keep current view
      return `${FUNNEL_ENDPOINT}?start_date=${dateRange.start}&end_date=${dateRange.end}&mode=${mode}`;
    }
    const presetDays = presetDaysFromId(range);
    if (presetDays) {
      return `${FUNNEL_ENDPOINT}?days=${presetDays}&mode=${mode}`;
    }
    if (dateRange.start && dateRange.end) {
      return `${FUNNEL_ENDPOINT}?start_date=${dateRange.start}&end_date=${dateRange.end}&mode=${mode}`;
    }
    return `${FUNNEL_ENDPOINT}?days=30&mode=${mode}`;
  }, [range, customDays, dateRange, metric]);

  useEffect(() => {
    if (!requestUrl) return undefined;
    let cancelled = false;

    const getFunnel = async () => {
      setLoading(true);
      try {
        const response = await GetAPIcalls(requestUrl, navigate);
        if (cancelled) return;
        if (response.status == 200 && response.data) {
          setApiFunnel(response.data);
          setApiError('');
        } else {
          setApiFunnel(null);
        }
      } catch (err) {
        if (cancelled) return;
        setApiFunnel(null); // graceful fallback to the static snapshot
        setApiError(err.message);
        console.error(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    getFunnel();
    return () => {
      cancelled = true;
    };
  }, [requestUrl, navigate]);

  const handleRangeChange = ({ id, start, end }) => {
    setRange(id);
    setDateRange({ start, end });
  };

  const mapped = useMemo(
    () => (apiFunnel ? mapFunnelPayload(apiFunnel) : null),
    [apiFunnel],
  );

  /* Static 30-day snapshot is only used while loading / if the call fails. */
  const scale = mapped ? 1 : days / baseDays;

  /* Funnel entries (API when available, else scaled static snapshot). */
  const derived = useMemo(() => {
    const source = mapped ? mapped.stages : FUNNEL;
    const scaled = source.map((f) => ({
      ...f,
      /* keep real zero-count stages at 0 — the bar gives them a sliver below */
      value: Math.max(0, Math.round(num(f.value) * scale)),
    }));
    const fallbackTotal = scaled.reduce((s, f) => s + f.value, 0);
    const total = mapped?.totals?.totalCustomers || fallbackTotal;
    return { scaled, total };
  }, [mapped, scale]);

  const { scaled: scaledFunnel, total } = derived;

  const pipeline =
    mapped?.totals?.inPipeline ??
    scaledFunnel
      .filter((f) => !f.label.includes('Approved'))
      .reduce((s, f) => s + f.value, 0);
  const approved =
    mapped?.totals?.approvedInWindow ??
    mapped?.totals?.approved ??
    scaledFunnel.find((f) => f.label === 'Approved')?.value ??
    0;
  const bottleneck = scaledFunnel.find((f) => f.bottleneck);

  /* Stage-to-stage conversion rate of the funnel entry. */
  const entry = scaledFunnel[0]?.value || 0;
  const conversionRate =
    mapped?.totals?.conversionPercent != null
      ? mapped.totals.conversionPercent
      : entry
        ? (approved / entry) * 100
        : 0;

  /* Median cycle (totals.median_cycle_days) split the way MiniStat shows it.
     null = no approved cases in the window → show "—" instead of a fake number. */
  const medianCycleRaw = mapped ? mapped.totals.medianCycleDays : 28.3;
  const hasMedian = Number.isFinite(Number(medianCycleRaw));
  const medianCycle = hasMedian ? Number(medianCycleRaw) : 0;
  const medianWhole = Math.floor(medianCycle);
  const medianFraction = Math.min(
    9,
    Math.max(0, Math.round((medianCycle - medianWhole) * 10)),
  );
  const medianBasis =
    mapped?.totals?.medianCycleBasis || 'stage entry → approval';

  const insight = mapped?.insight || null;

  /* Bar geometry. Rendered widths always add up to exactly 100% so the
     bottleneck flag, hover tooltip and stage tiles all line up with the
     segments that are actually drawn (previously zero-count stages were
     forced to 1 case, the bar overflowed 100% and flex shrank it, which
     pushed the "BOTTLENECK" flag off the striped stage). Zero-count stages
     keep a small sliver so they stay visible and clickable. */
  const sumValues = scaledFunnel.reduce((s, f) => s + f.value, 0);
  const emptyStages = scaledFunnel.filter((f) => f.value <= 0).length;
  const sliverTotal = Math.min(emptyStages * MIN_SEG_PCT, 20);
  const flexSpace = Math.max(100 - sliverTotal, 0);
  const flexScale = sumValues > 0 ? flexSpace / sumValues : 0;

  let acc = 0;
  const segs = scaledFunnel.map((f, idx) => {
    const width =
      f.value > 0
        ? f.value * flexScale
        : emptyStages
          ? sliverTotal / emptyStages
          : 0;
    const start = acc;
    acc += width;
    const prev = scaledFunnel[idx - 1];
    /* step_conversion_percent is null when it can't be computed (previous
       stage empty) → render "—" rather than inventing 0% or 100%. */
    const prevPct =
      f.stepConversion != null
        ? f.stepConversion
        : idx === 0
          ? 100
          : prev && prev.value > 0
            ? (f.value / prev.value) * 100
            : null;
    const sharePct =
      f.sharePercent != null
        ? f.sharePercent
        : sumValues
          ? (f.value / sumValues) * 100
          : 0;
    return {
      ...f,
      idx,
      start,
      width,
      prevPct,
      sharePct,
    };
  });

  const active = selected != null ? segs[selected] : null;

  /* Days handed to the stage-queue endpoint (7 / 30 / 90 only). */
  const queueDays = mapped
    ? closestApiDays(mapped.days)
    : (presetDaysFromId(range) ?? closestApiDays(days));

  const handleDownload = () => {
    const rows = [
      ['stage', 'value', 'share_%', 'conversion_from_prev_%'],
      ...segs.map((s) => [
        s.label,
        s.value,
        s.sharePct.toFixed(1),
        s.prevPct != null ? s.prevPct.toFixed(1) : '',
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fileRange =
      range === 'custom'
        ? `${dateRange.start || 'start'}-${dateRange.end || 'end'}`
        : range;
    a.download = `funnel-${fileRange}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 1600);
  };

  return (
    <Card delay={180} className="p-5 md:p-6 h-full">
      {/* ---------- Header ---------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className={`w-9 h-9 rounded-xl ${TONES.cyan.tile} flex items-center justify-center transition-transform duration-300 hover:scale-110`}
          >
            <Filter size={17} strokeWidth={1.9} />
          </span>
          <div>
            <h2 className="font-semibold text-slate-900">Onboarding funnel</h2>
            <p className="text-[11px] text-slate-400">
              Hover for detail · click a stage to inspect
            </p>
          </div>
        </div>

        {/* Filters: reusable date-range picker, metric & export */}
        <div
          className="flex w-full items-center justify-end gap-2 flex-wrap sm:w-auto"
          aria-busy={loading}
          title={apiError || undefined}
        >
          <DateRangePicker
            range={range}
            startDate={dateRange.start}
            endDate={dateRange.end}
            onChange={handleRangeChange}
          />

          <div className="flex items-center rounded-lg bg-slate-100/90 p-0.5 ring-1 ring-slate-200/70">
            {[
              { id: 'cases', label: 'Cases' },
              { id: 'share', label: 'Share' },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMetric(m.id)}
                className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-[11px] font-semibold transition-all duration-200 ${
                  metric === m.id
                    ? 'bg-white text-brand shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {m.id === 'share' && <Percent size={11} />}
                {m.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-300 bg-white text-[11px] font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-all active:scale-95"
          >
            {downloaded ? (
              <span className="text-emerald-500 font-bold">✓</span>
            ) : (
              <Download size={13} />
            )}
            CSV
          </button>
          {dateError && (
            <p className="basis-full text-left text-[11px] font-medium text-rose-600 sm:text-right">
              {dateError}
            </p>
          )}
        </div>
      </div>

      {/* ---------- Summary strip ---------- */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
        <MiniStat
          icon={Inbox}
          tone={TONES.blue}
          label="In pipeline"
          value={pipeline}
          suffix={pipeline === 1 ? 'case' : 'cases'}
          delay={200}
          sub={`of ${fmt(total)} total · ${rangeLabel}`}
        />
        <MiniStat
          icon={BadgeCheck}
          tone={TONES.green}
          label="Approved"
          value={approved}
          suffix={approved === 1 ? 'case' : 'cases'}
          delay={260}
          sub={`${conversionRate.toFixed(1)}% of ${
            mapped ? 'total customers' : 'entries'
          }`}
        />
        <MiniStat
          icon={Timer}
          tone={TONES.violet}
          label="Median cycle"
          value={hasMedian ? medianWhole : 0}
          display={hasMedian ? undefined : '—'}
          suffix={hasMedian ? `.${medianFraction}d` : undefined}
          delay={320}
          sub={hasMedian ? medianBasis : 'no sample in window'}
        />
        {/* <MiniStat
          icon={AlertTriangle}
          tone={TONES.amber}
          label="At SLA risk"
          value={bottleneck?.slaRisk || bottleneck?.value || 0}
          delay={380}
        /> */}
      </div>

      {/* ---------- Funnel bar ---------- */}
      <div className="relative mt-10">
        {/* Bottleneck flag */}
        {bottleneck && (
          <div
            className="absolute -top-7 -translate-x-1/2 flex flex-col items-center animate-fade-up"
            style={{
              left: `clamp(56px, ${segs.find((s) => s.bottleneck).start + segs.find((s) => s.bottleneck).width / 2}%, calc(100% - 56px))`,
              animationDelay: '700ms',
            }}
          >
            <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-[#B45309] bg-[#FFFBEB] ring-1 ring-[#FDE68A] rounded-full px-2 py-0.5 whitespace-nowrap shadow-sm">
              <AlertTriangle size={11} className="animate-pulse-dot" />
              BOTTLENECK
            </span>
            <span className="w-px h-2.5 bg-[#FCD34D]" />
          </div>
        )}

        {/* Hover tooltip */}
        {hover != null && (
          <div
            className="absolute -top-11 -translate-x-1/2 z-10 pointer-events-none animate-fade-up"
            style={{
              left: `clamp(70px, ${segs[hover].start + segs[hover].width / 2}%, calc(100% - 70px))`,
            }}
          >
            <div className="bg-ink text-white rounded-lg px-3 py-1.5 text-[11px] shadow-lift whitespace-nowrap">
              <span className="font-bold">{segs[hover].label}</span>{' '}
              <span className="text-white/70 tabular-nums">
                · {segs[hover].value} cases · {Math.round(segs[hover].sharePct)}
                %
              </span>
              <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-ink rotate-45" />
            </div>
          </div>
        )}

        <div className="flex h-4 gap-[3px]">
          {segs.map((s, i) => {
            const [c0, c1] = colorsOf(s);
            const isActive = hover === i || selected === i;
            return (
              <button
                key={s.label}
                aria-label={`${s.label}: ${s.value} cases`}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onClick={() => setSelected(selected === i ? null : i)}
                className={`seg-anim relative h-full rounded-[5px] cursor-pointer transition-all duration-300 ${
                  s.bottleneck ? 'stripes-anim' : ''
                } ${
                  hover != null && hover !== i && selected !== i
                    ? 'opacity-40'
                    : 'opacity-100'
                } ${isActive ? 'scale-y-125 shadow-sm' : ''}`}
                style={{
                  width: `${s.width}%`,
                  background: s.bottleneck
                    ? `repeating-linear-gradient(45deg, rgba(255,255,255,0.45) 0 5px, rgba(255,255,255,0) 5px 10px), linear-gradient(90deg, ${c0}, ${c1})`
                    : `linear-gradient(90deg, ${c0}, ${c1})`,
                  animationDelay: `${250 + i * 80}ms`,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* ---------- Stage tiles ---------- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mt-6">
        {segs.map((s, i) => {
          const [c0, c1] = colorsOf(s);
          const isSel = selected === i;
          return (
            <StageTile
              key={s.label}
              s={s}
              i={i}
              c0={c0}
              c1={c1}
              isSel={isSel}
              onHover={setHover}
              onSelect={() => setSelected(isSel ? null : i)}
              metric={metric}
            />
          );
        })}
      </div>

      {/* ---------- Stage detail explorer ---------- */}
      <div
        className={`grid transition-all duration-300 ease-out ${
          active
            ? 'grid-rows-[1fr] opacity-100 mt-4'
            : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          {active && (
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/70 p-4 flex flex-col md:flex-row md:items-center gap-4">
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ring-1"
                style={{
                  background: `linear-gradient(135deg, ${colorsOf(active)[0]}22, ${colorsOf(active)[1]}33)`,
                  color: colorsOf(active)[0],
                  '--tw-ring-color': `${colorsOf(active)[0]}44`,
                }}
              >
                <Filter size={18} strokeWidth={1.9} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-slate-800">
                    {active.label}
                  </p>
                  {active.bottleneck && (
                    <span className="text-[10px] font-bold tracking-wide text-[#B45309] bg-[#FFFBEB] ring-1 ring-[#FDE68A] rounded-full px-2 py-0.5">
                      BOTTLENECK
                    </span>
                  )}
                  <span className="text-[11px] font-semibold text-slate-400">
                    {active.prevPct != null
                      ? `${Math.round(active.prevPct)}% carry-through`
                      : 'carry-through n/a'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  {active.desc || 'No stage description available.'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 shrink-0">
                <div>
                  <p className="text-[10px] font-semibold tracking-wide text-slate-400 uppercase flex items-center gap-1">
                    <Timer size={10} /> Avg
                  </p>
                  <p className="text-xs font-bold text-slate-700 mt-0.5 tabular-nums">
                    {active.avg || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold tracking-wide text-slate-400 uppercase flex items-center gap-1">
                    <Hourglass size={10} /> Oldest
                  </p>
                  <p className="text-xs font-bold text-slate-700 mt-0.5 tabular-nums">
                    {active.oldest || '—'}
                  </p>
                </div>
                <div></div>
              </div>
              <button
                onClick={() => setQueueStage(active)}
                className="inline-flex items-center gap-1.5 shrink-0 text-xs font-semibold text-brand bg-white border border-slate-200 hover:border-brand/40 hover:bg-brand-soft rounded-lg px-3.5 py-2 transition-all duration-200 active:scale-[0.97] shadow-sm"
              >
                Open stage queue
                <ArrowRight size={12} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ---------- Insight banner ---------- */}
      {bottleneck && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/60 p-3.5">
          <span className="w-8 h-8 rounded-lg bg-amber-100 text-[#B45309] flex items-center justify-center shrink-0">
            <Lightbulb size={15} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">
              {insight?.title || 'Focus here for the biggest lift'}
            </p>
            <p className="text-sm text-slate-600 mt-0.5 leading-snug">
              {insight?.message ? (
                <>{insight.message} </>
              ) : (
                <>
                  <b>{bottleneck.label}</b> is the slowest stage (
                  {bottleneck.avg} avg) holding{' '}
                  <b>{Math.round(bottleneck.sharePct || 0)}%</b> of all
                  cases.{' '}
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* ---------- How to read ---------- */}
      {/* <div className="mt-3 rounded-xl border border-slate-200/70 bg-slate-50/50 overflow-hidden">
        <button
          onClick={() => setFold((f) => !f)}
          className="w-full flex items-center gap-2 px-3.5 py-3 text-sm font-semibold text-slate-700 hover:text-brand transition-colors"
        >
          <Info size={15} className="text-brand" />
          How to read this funnel
          <ChevronDown
            size={16}
            className={`ml-auto text-slate-400 transition-transform duration-200 ${fold ? 'rotate-180' : ''}`}
          />
        </button>
        {fold && (
          <div className="px-3.5 pb-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-600">
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-brand shrink-0" />
              Each block is a stage; its width is the share of cases in it.
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-brand shrink-0" />
              <b>Carry-through</b> shows the % that move on from the previous
              stage.
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-amber-500 shrink-0" />
              The striped block flags the <b>bottleneck</b> stage.
            </div>
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-brand shrink-0" />
              Click a stage to open its detail and queue; hover for a quick
              tooltip.
            </div>
          </div>
        )}
      </div> */}

      {/* Stage queue slide-over */}
      {queueStage && (
        <StageQueueDrawer
          key={queueStage.key || queueStage.label}
          stage={queueStage}
          days={queueDays}
          onClose={() => setQueueStage(null)}
        />
      )}
    </Card>
  );
}

/* Individual stage tile with count-up */
function StageTile({ s, i, c0, c1, isSel, onHover, onSelect, metric }) {
  const n = useCountUp(s.value, 800 + i * 90);
  const big = metric === 'share' ? `${s.sharePct.toFixed(0)}%` : n;
  return (
    <button
      onMouseEnter={() => onHover(i)}
      onMouseLeave={() => onHover(null)}
      onClick={onSelect}
      className={`group animate-fade-up text-left rounded-xl p-3 border transition-all duration-200 cursor-pointer ${
        isSel
          ? 'border-brand/40 bg-brand-soft/60 ring-1 ring-brand/20 shadow-card'
          : s.bottleneck
            ? 'bg-[#FFFBEB]/70 border-[#FDE68A] hover:shadow-card'
            : 'border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-card hover:-translate-y-0.5'
      }`}
      style={{ animationDelay: `${320 + i * 60}ms` }}
    >
      <div className="flex items-center justify-between gap-1">
        <span
          className="w-2.5 h-2.5 rounded-[4px] transition-transform duration-200 group-hover:scale-125 shrink-0"
          style={{ background: `linear-gradient(135deg, ${c0}, ${c1})` }}
        />
        <span className="text-[10px] font-bold text-slate-400 tabular-nums">
          {s.prevPct != null ? `${Math.round(s.prevPct)}%` : '—'}
        </span>
      </div>
      <p className="mt-1.5 text-[11px] font-medium text-slate-500 leading-tight">
        {s.label}
      </p>
      <p className="mt-2 text-xl font-bold text-slate-900 tabular-nums leading-6">
        {big}
      </p>
      <p
        className={`text-[11px] mt-0.5 ${
          s.subTone === 'risk' ? 'text-[#B45309] font-bold' : 'text-slate-400'
        }`}
      >
        {metric === 'share' ? 'of total' : s.sub}
      </p>
    </button>
  );
}
